import IBloc from '../../base/IBloc';
import { 
  VoucherWithCodes, 
  CreateVoucherRequest, 
  UpdateVoucherRequest, 
  VoucherFilters,
  VoucherStats,
  VoucherCode
} from '../../types/voucher.types';
import { VoucherManagementService, VoucherListResponse, VoucherCodeListResponse } from '../../service/supabase/VoucherManagementService';

export interface VoucherViewState {
  // Data state
  vouchers: VoucherWithCodes[];
  selectedVoucher: VoucherWithCodes | null;
  voucherCodes: VoucherCode[];
  stats: VoucherStats | null;
  
  // UI state
  loading: boolean;
  error: string | null;
  hasData: boolean;
  
  // Form state
  isFormOpen: boolean;
  formType: 'create' | 'edit' | null;
  
  // Codes modal state
  isCodesModalOpen: boolean;
  
  // Filter state
  filters: VoucherFilters;
  
  // Pagination
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  
  // Codes pagination
  codesPagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export class BlocManagementVouchers extends IBloc {
  /**
   * State chính của Voucher Management
   */
  info: VoucherViewState = {
    vouchers: [],
    selectedVoucher: null,
    voucherCodes: [],
    stats: null,
    loading: false,
    error: null,
    hasData: false,
    isFormOpen: false,
    formType: null,
    isCodesModalOpen: false,
    filters: {},
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      totalPages: 0
    },
    codesPagination: {
      page: 1,
      limit: 50,
      total: 0,
      totalPages: 0
    }
  };

  /**
   * Update state helper
   */
  private updateState(updates: Partial<VoucherViewState>): void {
    this.info = {
      ...this.info,
      ...updates
    };
    this.setStream('voucherViewState', this.info);
  }

  // Initialize data
  async initialize(): Promise<void> {
    await Promise.all([
      this.loadVouchers(),
      this.loadStats()
    ]);
  }

  // Load vouchers with filters and pagination
  async loadVouchers(): Promise<void> {
    try {
      this.updateState({ loading: true, error: null });
      
      const response: VoucherListResponse = await VoucherManagementService.getVouchers(
        this.info.pagination.page,
        this.info.pagination.limit,
        this.info.filters
      );
      
      this.updateState({
        vouchers: response.data,
        pagination: {
          page: response.page,
          limit: response.limit,
          total: response.total,
          totalPages: response.totalPages
        },
        hasData: response.data.length > 0,
        loading: false
      });
    } catch (error) {
      console.error('Error loading vouchers:', error);
      this.updateState({
        error: error instanceof Error ? error.message : 'Không thể tải danh sách voucher',
        loading: false
      });
    }
  }

  // Load statistics
  async loadStats(): Promise<void> {
    try {
      const stats = await VoucherManagementService.getVoucherStats();
      this.updateState({ stats });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }

  // Set filters
  setFilters(filters: VoucherFilters): void {
    this.updateState({
      filters: { ...this.info.filters, ...filters },
      pagination: { ...this.info.pagination, page: 1 }
    });
    this.loadVouchers();
  }

  // Reset filters
  resetFilters(): void {
    this.updateState({
      filters: {},
      pagination: { ...this.info.pagination, page: 1 }
    });
    this.loadVouchers();
  }

  // Pagination
  goToPage(page: number): void {
    this.updateState({
      pagination: { ...this.info.pagination, page }
    });
    this.loadVouchers();
  }

  // Refresh data
  async refreshVouchers(): Promise<void> {
    await this.loadVouchers();
    await this.loadStats();
  }

  // Form management
  openCreateForm(): void {
    this.updateState({
      isFormOpen: true,
      formType: 'create',
      selectedVoucher: null,
      error: null
    });
  }

  openEditForm(voucher: VoucherWithCodes): void {
    this.updateState({
      isFormOpen: true,
      formType: 'edit',
      selectedVoucher: voucher,
      error: null
    });
  }

  closeForm(): void {
    this.updateState({
      isFormOpen: false,
      formType: null,
      selectedVoucher: null,
      error: null
    });
  }

  // CRUD operations
  async createVoucher(data: CreateVoucherRequest): Promise<void> {
    try {
      this.updateState({ loading: true, error: null });
      
      await VoucherManagementService.createVoucher(data);
      
      this.updateState({ loading: false, isFormOpen: false, formType: null });
      await this.refreshVouchers();
    } catch (error) {
      console.error('Error creating voucher:', error);
      this.updateState({
        error: error instanceof Error ? error.message : 'Không thể tạo voucher',
        loading: false
      });
    }
  }

  async updateVoucher(data: UpdateVoucherRequest): Promise<void> {
    try {
      this.updateState({ loading: true, error: null });
      
      await VoucherManagementService.updateVoucher(data);
      
      this.updateState({ loading: false, isFormOpen: false, formType: null });
      await this.refreshVouchers();
    } catch (error) {
      console.error('Error updating voucher:', error);
      this.updateState({
        error: error instanceof Error ? error.message : 'Không thể cập nhật voucher',
        loading: false
      });
    }
  }

  async deleteVoucher(id: string): Promise<void> {
    try {
      this.updateState({ loading: true, error: null });
      
      await VoucherManagementService.deleteVoucher(id);
      
      this.updateState({ loading: false });
      await this.refreshVouchers();
    } catch (error) {
      console.error('Error deleting voucher:', error);
      this.updateState({
        error: error instanceof Error ? error.message : 'Không thể xóa voucher',
        loading: false
      });
    }
  }

  async cloneVoucher(id: string): Promise<void> {
    try {
      this.updateState({ loading: true, error: null });
      
      await VoucherManagementService.cloneVoucher(id);
      
      this.updateState({ loading: false });
      await this.refreshVouchers();
    } catch (error) {
      console.error('Error cloning voucher:', error);
      this.updateState({
        error: error instanceof Error ? error.message : 'Không thể sao chép voucher',
        loading: false
      });
    }
  }

  // Voucher codes management
  async openCodesModal(voucher: VoucherWithCodes): Promise<void> {
    this.updateState({
      selectedVoucher: voucher,
      isCodesModalOpen: true,
      codesPagination: { ...this.info.codesPagination, page: 1 }
    });
    await this.loadVoucherCodes();
  }

  closeCodesModal(): void {
    this.updateState({
      isCodesModalOpen: false,
      selectedVoucher: null,
      voucherCodes: []
    });
  }

  async loadVoucherCodes(): Promise<void> {
    if (!this.info.selectedVoucher) return;

    try {
      this.updateState({ loading: true, error: null });
      
      const response: VoucherCodeListResponse = await VoucherManagementService.getVoucherCodes(
        this.info.selectedVoucher.id,
        this.info.codesPagination.page,
        this.info.codesPagination.limit
      );
      
      this.updateState({
        voucherCodes: response.data,
        codesPagination: {
          page: response.page,
          limit: response.limit,
          total: response.total,
          totalPages: response.totalPages
        },
        loading: false
      });
    } catch (error) {
      console.error('Error loading voucher codes:', error);
      this.updateState({
        error: error instanceof Error ? error.message : 'Không thể tải danh sách mã voucher',
        loading: false
      });
    }
  }

  async generateCodes(numberOfCodes: number, prefix?: string): Promise<void> {
    if (!this.info.selectedVoucher) return;

    try {
      this.updateState({ loading: true, error: null });
      
      await VoucherManagementService.generateRandomCodes(
        this.info.selectedVoucher.id,
        numberOfCodes,
        prefix
      );
      
      this.updateState({ loading: false });
      await this.loadVoucherCodes();
    } catch (error) {
      console.error('Error generating codes:', error);
      this.updateState({
        error: error instanceof Error ? error.message : 'Không thể tạo mã voucher',
        loading: false
      });
    }
  }

  async deleteVoucherCode(codeId: string): Promise<void> {
    if (!this.info.selectedVoucher) return;

    try {
      this.updateState({ loading: true, error: null });
      
      await VoucherManagementService.deleteVoucherCode(this.info.selectedVoucher.id, codeId);
      
      this.updateState({ loading: false });
      await this.loadVoucherCodes();
    } catch (error) {
      console.error('Error deleting voucher code:', error);
      this.updateState({
        error: error instanceof Error ? error.message : 'Không thể xóa mã voucher',
        loading: false
      });
    }
  }

  // Codes pagination
  goToCodesPage(page: number): void {
    this.updateState({
      codesPagination: { ...this.info.codesPagination, page }
    });
    this.loadVoucherCodes();
  }

  // Clear error
  clearError(): void {
    this.updateState({ error: null });
  }
}
